import * as React from 'react';
import { ArrowUp } from '../../primitives/Icon/icons';
import { Icon } from '../../primitives/Icon/Icon';
import { useId } from 'react';
import {
  root as rootCls,
  textArea as textAreaCls,
  actionRow as actionRowCls,
  actionLeft as actionLeftCls,
  actionRight as actionRightCls,
  sendBtn as sendBtnCls,
  srLabel as srLabelCls,
  prominenceRoot,
  prominenceTextArea,
  prominenceSend,
} from './Composer.css';

/**
 * How strongly the composer asserts itself as the primary input — a single
 * dial of increasing focal weight for surfaces where it would otherwise recede
 * into the panel (the Create-New-Workstream usability defect).
 *   - `flat`      — the calm baseline (bgSurface, faint placeholder). Default.
 *   - `raised`    — a white card lifted off the panel grey with a soft shadow.
 *   - `outlined`  — a crisp frame that flips to accent on focus; lightest touch.
 *   - `spotlight` — a white focal bar with an accent beacon frame + accent send.
 */
export type ComposerProminence = 'flat' | 'raised' | 'outlined' | 'spotlight';

export interface ComposerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Bound value of the textarea. Composer is uncontrolled if omitted. */
  value?: string;
  /** Default value when uncontrolled. */
  defaultValue?: string;
  /** Fires on every change. */
  onChange?: (value: string) => void;
  /** Fires when the user activates send (button click, Enter, or Cmd/Ctrl+Enter). */
  onSend?: (value: string) => void;
  /** Placeholder for the textarea. */
  placeholder?: string;
  /** Optional left-side action buttons (paperclip, lightning, etc.). */
  leadingActions?: React.ReactNode;
  /**
   * Disables the send button (controlled). When omitted, send is
   * disabled iff value is empty / whitespace-only.
   */
  sendDisabled?: boolean;
  /** Aria-label for the send button. Defaults to "Send". */
  sendLabel?: string;
  /** Form-control name forwarded to the underlying `<textarea>`. */
  name?: string;
  /** DOM `id` forwarded to the underlying `<textarea>` (for label `htmlFor`). */
  textareaId?: string;
  /** Aria-label forwarded to the underlying `<textarea>` (for screen readers). */
  textareaAriaLabel?: string;
  /**
   * How strongly the composer reads as the primary input (see
   * {@link ComposerProminence}). Defaults to `flat` — the calm baseline. Raise
   * it on surfaces where the composer must clearly be the place to type.
   */
  prominence?: ComposerProminence;
  /** Forwarded ref to the underlying `<textarea>` (for focus / autofocus). */
  textareaRef?: React.Ref<HTMLTextAreaElement>;
  /** Composer root ref. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Composer — chat input. Borderless multiline textarea above an action
 * row containing optional leading buttons (paperclip / lightning / etc.)
 * and a filled-square send button on the right.
 *
 * **Behaviour:**
 *   - Uncontrolled by default; pass `value` + `onChange` to control.
 *   - Enter submits via `onSend(value)`.
 *   - Shift+Enter inserts a new line.
 *   - Cmd/Ctrl+Enter also submits (alternative shortcut).
 *   - Send button disabled when value is empty / whitespace-only
 *     (override via `sendDisabled`).
 *   - Textarea auto-resizes 1 → 6 rows; beyond that it internal-scrolls
 *     so the composer never pushes the chat off screen.
 *
 * Prototype: `http://localhost:8000/` chat composer. Validated 2026-05-01.
 */
export function Composer({
  value,
  defaultValue,
  onChange,
  onSend,
  placeholder = 'Ask, instruct, or update the team…',
  leadingActions,
  sendDisabled,
  sendLabel = 'Send',
  name,
  textareaId,
  textareaAriaLabel,
  prominence = 'flat',
  textareaRef,
  ref,
  className,
  ...rest
}: ComposerProps) {
  const [internal, setInternal] = React.useState(defaultValue ?? '');
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;
  const trimmed = current.trim();
  const resolvedDisabled = sendDisabled ?? trimmed.length === 0;

  // Guarantee a non-empty accessible name for the textarea. `placeholder`
  // is NOT a reliable accessible name (AT may ignore it, and it disappears
  // once the user types), so we always associate a visually-hidden
  // `<label>` and also set an explicit `aria-label`. Precedence:
  // `textareaAriaLabel` → `placeholder` → 'Message'.
  const reactId = useId();
  const fieldId = textareaId ?? reactId;
  const accessibleName = textareaAriaLabel ?? (placeholder.trim() || 'Message');

  // Auto-resize: each render, snap height back to scrollHeight. The
  // visible cap (~6 rows / 96px) is enforced by `max-height` on the
  // textarea CSS recipe; beyond that the textarea internal-scrolls.
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
  React.useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [current]);

  const setRefs = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof textareaRef === 'function') textareaRef(node);
      else if (textareaRef && 'current' in textareaRef) textareaRef.current = node;
    },
    [textareaRef],
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) setInternal(e.target.value);
    onChange?.(e.target.value);
  };

  const handleSend = () => {
    if (resolvedDisabled) return;
    onSend?.(current);
    // Clear the textarea after sending
    if (!isControlled) setInternal('');
    onChange?.('');
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter submits (alternative shortcut)
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
      return;
    }

    // Enter (without Shift) submits
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    // Shift+Enter allows default behavior (new line)
  };

  const cls = [rootCls, prominenceRoot[prominence], className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} {...rest}>
      <label htmlFor={fieldId} className={srLabelCls}>
        {accessibleName}
      </label>
      <textarea
        ref={setRefs}
        id={fieldId}
        name={name}
        className={[textAreaCls, prominenceTextArea[prominence]].join(' ')}
        value={current}
        onChange={handleChange}
        onKeyDown={handleKey}
        placeholder={placeholder}
        aria-label={accessibleName}
        rows={1}
      />
      <div className={actionRowCls}>
        {leadingActions && <div className={actionLeftCls}>{leadingActions}</div>}
        <div className={actionRightCls}>
          <button
            type="button"
            className={[sendBtnCls, prominenceSend[prominence]].join(' ')}
            disabled={resolvedDisabled}
            aria-label={sendLabel}
            onClick={handleSend}
          >
            <Icon as={ArrowUp} size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
Composer.displayName = 'Composer';
